/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

import ContentFrame from '../../applicationFrame/ContentFrame';
import { ScrollAwareContext, ScrollAwareScroller, ScrollAwareAppNavStep } from '../../components/ScrollAware';
import { AppHeaderScrollableTextWithLanguage } from '../../components/appNav/AppHeaderScrollable';

import LoginPassword from '../contentViews/LoginPassword';
import Login from '../contentViews/Login';

import { validateEmail } from '../../../utilities/Validators';
import tokenDurations from '../../../constants/tokenDurations';
import AppUtils from '../../../utilities/AppUtils';
import { setSignupPresented, isMobileApplication } from '../../../utilities/MobileUtil';
import { authToken } from '../../../utilities/LocalStorage';
import UserModel from '../../../models/UserModel';
import { loginWithPendingIntent } from './pendingIntendedGroup';


class LoginController extends React.Component {

  constructor(props) {
    super(props);
    this.forceKeepSignedIn = isMobileApplication();
    this.onChangeEmail = this.onChangeEmail.bind(this);
    this.onChangePassword = this.onChangePassword.bind(this);
    this.submitEmailForm = this.submitEmailForm.bind(this);
    this.submitPasswordForm = this.submitPasswordForm.bind(this);
    this.toggleKeepSignedIn = this.toggleKeepSignedIn.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.goBack = this.goBack.bind(this);

    this.state = {
      email: props.email,
      password: '',
      isEmailValid: validateEmail(props.email),
      isPasswordValid: false,
      keepSignedIn: false,
      errorMessage: null,
      goToIntro: null,
    };
  }

  UNSAFE_componentWillMount() {
    if (IS_ANDROID_BUILD) {
      setSignupPresented(() => {
        this.setState({ goToIntro: true });
      }, () => {
        this.setState({ goToIntro: false });
      });
    } else {
      this.setState({ goToIntro: false });
    }
  }

  onChangeEmail(email) {
    this.props.updateEmail(email);
    this.setState({
      email,
      isEmailValid: validateEmail(email),
    });
  }

  onChangePassword(e) {
    const value = e.target.value;
    this.setState({
      password: value,
      isPasswordValid: value.length > 1,
    });
  }

  onFocus() {
    this.setState({
      errorMessage: null,
    });
  }

  submitEmailForm(e) {
    e.preventDefault();

    this.setState({
      errorMessage: this.state.isEmailValid ? null : <FormattedMessage id="error.email-validator.invalid-email.message" />,
    });

    if (this.state.isEmailValid) {
      this.props.goToPassword();
    }
  }

  submitPasswordForm(e) {
    e.preventDefault();
    if (!this.state.isEmailValid || !this.state.isPasswordValid) { return; }

    const duration = this.state.keepSignedIn || this.forceKeepSignedIn ? tokenDurations.LONG : tokenDurations.SHORT;

    this.props.submitCredentials({
      email: this.state.email,
      password: this.state.password,
      duration,
    }, (apiError) => {
      if (!apiError) {
        const sessionData = AppUtils.extractDataFromAuthToken(authToken.get());
        const user = new UserModel(sessionData.user);

        if (user.isMfaEnabled()) {
          this.props.updateUser(user.data);
          this.props.goToMfaVerify(duration);
        } else {
          loginWithPendingIntent(user.data, this.props.submitUserUpdate);
        }
      } else if (apiError && apiError.messageKey()) {
        this.setState({
          errorMessage: <FormattedMessage id={apiError.messageKey()} />,
        });
      }
    });
  }

  toggleKeepSignedIn() {
    this.setState({
      keepSignedIn: !this.state.keepSignedIn,
    });
  }

  goBack() {
    this.setState({
      isEmailSubmitted: false,
      password: '',
    });
    this.props.goToLogin();
  }

  render() {
    if (this.state.goToIntro === null) {
      return <div />;
    } else if (this.state.goToIntro) {
      return <Redirect to="/intro" />;
    }

    const isEmailSubmitted = this.state.isEmailSubmitted || (this.props.startWithPassword && this.state.email);
    let goBack = null;
    if (isEmailSubmitted) {
      goBack = this.goBack;
    }

    return (
      <div>
        <ScrollAwareContext>
          <ScrollAwareAppNavStep
            next={isEmailSubmitted ? this.submitPasswordForm : this.submitEmailForm}
            goBack={goBack}
            submitDisabled={isEmailSubmitted ? !this.state.isPasswordValid : !this.state.isEmailValid}
            color="white"
            signUp={isEmailSubmitted ? undefined : this.props.goToSignup}
          />

          <ContentFrame>
            <ScrollAwareScroller>
              <AppHeaderScrollableTextWithLanguage
                id="login.title.text"
                allowLanguageToggle={!IS_ANDROID_BUILD}
              />

              {
                isEmailSubmitted ? (
                  <LoginPassword
                    email={this.state.email}
                    password={this.state.password}
                    keepSignedIn={this.state.keepSignedIn}
                    onChangePassword={this.onChangePassword}
                    onSubmit={this.submitPasswordForm}
                    isPasswordValid={!!this.state.errorMessage}
                    toggleKeepSignedIn={this.toggleKeepSignedIn}
                    errorMessage={this.state.errorMessage}
                    onFocus={this.onFocus}
                    goToForgotPassword={this.props.goToForgotPassword}
                    forceKeepSignedIn={this.forceKeepSignedIn}
                  />
                ) : (
                  <Login
                    email={this.state.email}
                    onChangeEmail={this.onChangeEmail}
                    onSubmit={this.submitEmailForm}
                    errorMessage={this.state.errorMessage}
                    onFocus={this.onFocus}
                    goToSignup={this.props.goToSignup}
                  />
                )
              }
            </ScrollAwareScroller>
          </ContentFrame>
        </ScrollAwareContext>
      </div>
    );
  }
}
LoginController.propTypes = {
  email: PropTypes.string,
  startWithPassword: PropTypes.bool,
  onChangeEmail: PropTypes.func,
  updateEmail: PropTypes.func,
  goToForgotPassword: PropTypes.func,
  goToSignup: PropTypes.func,
  goToMfaVerify: PropTypes.func,
  goToPassword: PropTypes.func,
  goToLogin: PropTypes.func,
  submitCredentials: PropTypes.func,
  updateUser: PropTypes.func,
  submitUserUpdate: PropTypes.func,
};

export default LoginController;
